import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

interface Projeto {
  id?: string;
  nome: string;
  objetivo: string;
  prazo: string;
  valor_maximo: string;
  id_cliente: string;
  id_empresa: string;
  id_fornecedor: string;
  id_local: string;
}

interface Cliente {
  id: string;
  nome: string;
}

interface Empresa {
  id: string;
  razao_social: string;
}

interface Fornecedor {
  id: string;
  razao_social: string;
}

interface Local {
  id: string;
  nome: string;
}

export default function ProjetoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isEdit = !!id;

  const [projeto, setProjeto] = useState<Projeto>({
    nome: "",
    objetivo: "",
    prazo: "",
    valor_maximo: "",
    id_cliente: "",
    id_empresa: "",
    id_fornecedor: "",
    id_local: ""
  });

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadRelatedData();
    if (isEdit) {
      loadProjeto();
    } else {
      setLoadingData(false);
    }
  }, [id]);

  const loadRelatedData = async () => {
    try {
      // @ts-ignore - Temporary fix for deep recursion type error
      const clientesPromise = supabase.from("clientes").select("id, nome").eq("ativo", true);
      // @ts-ignore - Temporary fix for deep recursion type error
      const empresasPromise = supabase.from("empresas").select("id, razao_social").eq("ativo", true);
      // @ts-ignore - Temporary fix for deep recursion type error
      const fornecedoresPromise = supabase.from("fornecedores").select("id, razao_social").eq("ativo", true);
      // @ts-ignore - Temporary fix for deep recursion type error
      const locaisPromise = supabase.from("locais").select("id, nome").eq("ativo", true);
      
      const [clientesRes, empresasRes, fornecedoresRes, locaisRes] = await Promise.all([
        clientesPromise,
        empresasPromise,
        fornecedoresPromise,
        locaisPromise
      ]);

      if (clientesRes.data) setClientes(clientesRes.data);
      if (empresasRes.data) setEmpresas(empresasRes.data);
      if (fornecedoresRes.data) setFornecedores(fornecedoresRes.data);
      if (locaisRes.data) setLocais(locaisRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados relacionados:", error);
    }
  };

  const loadProjeto = async () => {
    try {
      const { data, error } = await supabase
        .from("projetos")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProjeto({
          id: data.id,
          nome: data.nome || "",
          objetivo: data.objetivo || "",
          prazo: data.prazo || "",
          valor_maximo: data.valor_maximo?.toString() || "",
          id_cliente: data.id_cliente || "",
          id_empresa: data.id_empresa || "",
          id_fornecedor: data.id_fornecedor || "",
          id_local: data.id_local || ""
        });
      }
    } catch (error) {
      console.error("Erro ao carregar projeto:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do projeto",
        variant: "destructive"
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projeto.nome.trim()) {
      toast({
        title: "Erro de validação",
        description: "O nome do projeto é obrigatório",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const projetoData = {
        nome: projeto.nome,
        objetivo: projeto.objetivo || null,
        prazo: projeto.prazo || null,
        valor_maximo: projeto.valor_maximo ? parseFloat(projeto.valor_maximo) : null,
        id_cliente: projeto.id_cliente || null,
        id_empresa: projeto.id_empresa || null,
        id_fornecedor: projeto.id_fornecedor || null,
        id_local: projeto.id_local || null,
        ...(isEdit ? { updated_by: user?.id } : { created_by: user?.id })
      };

      if (isEdit) {
        const { error } = await supabase
          .from("projetos")
          .update(projetoData)
          .eq("id", id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Projeto atualizado com sucesso"
        });
      } else {
        const { error } = await supabase
          .from("projetos")
          .insert([projetoData]);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Projeto criado com sucesso"
        });
      }

      navigate("/intranet/propostas/projetos");
    } catch (error) {
      console.error("Erro ao salvar projeto:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar projeto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/intranet/propostas/projetos")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? "Editar Projeto" : "Novo Projeto"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Edite as informações do projeto" : "Cadastre um novo projeto"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="dados-basicos" className="space-y-4">
          <TabsList>
            <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
            <TabsTrigger value="relacionamentos">Relacionamentos</TabsTrigger>
          </TabsList>

          <TabsContent value="dados-basicos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Informações Básicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={projeto.nome}
                    onChange={(e) => setProjeto({ ...projeto, nome: e.target.value })}
                    placeholder="Nome do projeto"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="objetivo">Objetivo</Label>
                  <Textarea
                    id="objetivo"
                    value={projeto.objetivo}
                    onChange={(e) => setProjeto({ ...projeto, objetivo: e.target.value })}
                    placeholder="Objetivo do projeto"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prazo">Prazo</Label>
                    <Input
                      id="prazo"
                      type="date"
                      value={projeto.prazo}
                      onChange={(e) => setProjeto({ ...projeto, prazo: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="valor_maximo">Valor Máximo</Label>
                    <Input
                      id="valor_maximo"
                      type="number"
                      step="0.01"
                      value={projeto.valor_maximo}
                      onChange={(e) => setProjeto({ ...projeto, valor_maximo: e.target.value })}
                      placeholder="0,00"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="relacionamentos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Vínculos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="id_cliente">Cliente</Label>
                    <Select
                      value={projeto.id_cliente}
                      onValueChange={(value) => setProjeto({ ...projeto, id_cliente: value || "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map((cliente) => (
                          <SelectItem key={cliente.id} value={cliente.id}>
                            {cliente.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="id_empresa">Empresa</Label>
                    <Select
                      value={projeto.id_empresa}
                      onValueChange={(value) => setProjeto({ ...projeto, id_empresa: value || "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {empresas.map((empresa) => (
                          <SelectItem key={empresa.id} value={empresa.id}>
                            {empresa.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="id_fornecedor">Fornecedor</Label>
                    <Select
                      value={projeto.id_fornecedor}
                      onValueChange={(value) => setProjeto({ ...projeto, id_fornecedor: value || "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um fornecedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {fornecedores.map((fornecedor) => (
                          <SelectItem key={fornecedor.id} value={fornecedor.id}>
                            {fornecedor.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="id_local">Local</Label>
                    <Select
                      value={projeto.id_local}
                      onValueChange={(value) => setProjeto({ ...projeto, id_local: value || "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um local" />
                      </SelectTrigger>
                      <SelectContent>
                        {locais.map((local) => (
                          <SelectItem key={local.id} value={local.id}>
                            {local.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/intranet/propostas/projetos")}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Salvando..." : isEdit ? "Atualizar" : "Criar"}
          </Button>
        </div>
      </form>
    </div>
  );
}